/**
 * FASE 21.6 — ACTION INTENT EXTRACTION
 *
 * Detects mutation intent from natural language + entity context.
 * Returns intent + extracted fields + missing fields.
 *
 * INVARIANT: Never executes. Only resolves.
 */

import { getMutationSchemaById, getAllMutationSchemas } from "./mutationSchemas";
import { getMissingFields }                              from "./validateMutationPayload";
import { getLocalDateString }                            from "../date";

/* ─── Intent patterns ────────────────────────────────── */

const INTENT_PATTERNS = [
  {
    toolId:      "create_visit",
    patterns:    [/registra.*visita/i, /crea.*visita/i, /anota.*visita/i, /nueva.*visita/i, /visitar/i, /hice.*visita/i, /realicé.*visita/i],
    confidence:  0.85,
  },
  {
    toolId:      "create_prayer",
    patterns:    [/registra.*oraci[oó]n/i, /crea.*oraci[oó]n/i, /agrega.*oraci[oó]n/i, /oración.*para/i, /petición/i, /intercede/i],
    confidence:  0.85,
  },
  {
    toolId:      "create_event",
    patterns:    [/crea.*evento/i, /agrega.*evento/i, /programa.*evento/i, /nuevo.*evento/i, /crear.*actividad/i],
    confidence:  0.75,
  },
  {
    toolId:      "create_family_note",
    patterns:    [/agrega.*nota/i, /escribe.*nota/i, /registra.*nota/i, /nueva.*nota/i, /anota esto/i, /guarda esta nota/i],
    confidence:  0.80,
  },
  {
    toolId:      "schedule_followup",
    patterns:    [/programa.*seguimiento/i, /agenda.*seguimiento/i, /seguimiento.*para/i, /visita.*seguimiento/i, /agendar.*visita/i],
    confidence:  0.80,
  },
  {
    toolId:      "update_visit_status",
    patterns:    [/marca.*visita.*como/i, /visita.*completada/i, /visita.*realizada/i, /completar.*visita/i, /marcar.*realizada/i],
    confidence:  0.75,
  },
  {
    toolId:      "update_person_status",
    patterns:    [/actualiza.*estado/i, /cambiar.*estado.*bautismo/i, /bautismo.*de/i, /estado.*espiritual/i],
    confidence:  0.70,
  },
];

/* ─── Field extractors ────────────────────────────────── */

function extractDate(text) {
  const today  = getLocalDateString();
  if (/hoy/i.test(text)) return today;
  // YYYY-MM-DD
  const ymd = text.match(/\b(\d{4}-\d{2}-\d{2})\b/);
  if (ymd) return ymd[1];
  // DD/MM/YYYY or DD-MM-YYYY
  const dmy = text.match(/\b(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})\b/);
  if (dmy) return `${dmy[3]}-${dmy[2].padStart(2, "0")}-${dmy[1].padStart(2, "0")}`;
  return null;
}

function extractVisitType(text) {
  if (/seguimiento/i.test(text))  return "seguimiento";
  if (/bienestar/i.test(text))    return "bienestar";
  if (/evangelismo/i.test(text))  return "evangelismo";
  if (/pastoral/i.test(text))     return "pastoral";
  return null;
}

function extractPrayerType(text) {
  if (/sanidad|enferm/i.test(text))       return "sanidad";
  if (/familia/i.test(text))              return "familia";
  if (/trabajo|empleo/i.test(text))       return "trabajo";
  if (/espiritual/i.test(text))           return "espiritual";
  if (/intercesi[oó]n/i.test(text))       return "intercesion";
  return "peticion_general";
}

function extractContactName(text, entityContext) {
  // Prefer entity name from context
  if (entityContext?.name) return entityContext.name;
  // Look for "para [Name]" or "de [Name]" patterns
  const forMatch = text.match(/(?:para|de)\s+(?:la\s+familia\s+)?([A-ZÁÉÍÓÚÑ][a-záéíóúñ]+(?:\s+[A-ZÁÉÍÓÚÑ][a-záéíóúñ]+)*)/);
  if (forMatch) return forMatch[1];
  return null;
}

/* ─── Field extraction router ─────────────────────────── */

function extractFields(toolId, text, entityContext) {
  const fields = {};

  switch (toolId) {
    case "create_visit":
    case "schedule_followup": {
      const name = extractContactName(text, entityContext);
      if (name) fields.contact_name = name;

      const date = extractDate(text);
      if (date) {
        if (toolId === "schedule_followup") fields.followup_date = date;
        else fields.date = date;
      } else if (toolId === "create_visit") {
        // Default to today for "create_visit" if user says "hice una visita"
        if (/hice|realicé|visité/i.test(text)) fields.date = getLocalDateString();
      }

      const vtype = extractVisitType(text);
      if (vtype) fields.visit_type = vtype;

      // Carry entity context
      if (entityContext?.type === "family" && entityContext?.id) fields.target_family_id = entityContext.id;
      if (entityContext?.type === "person" && entityContext?.id) fields.target_person_id = entityContext.id;
      break;
    }

    case "create_prayer": {
      const name = extractContactName(text, entityContext);
      if (name) fields.contact_name = name;
      fields.prayer_type = extractPrayerType(text);
      if (entityContext?.type === "person" && entityContext?.id) fields.target_person_id = entityContext.id;
      if (entityContext?.type === "family" && entityContext?.id) fields.target_family_id = entityContext.id;
      break;
    }

    case "create_event": {
      const date = extractDate(text);
      if (date) fields.date = date;
      // Minimal extraction — user must provide title
      break;
    }

    case "create_family_note": {
      if (entityContext?.id)   fields.entity_id   = entityContext.id;
      if (entityContext?.type) fields.entity_type = entityContext.type;
      break;
    }

    case "update_visit_status": {
      if (/realizada|completada/i.test(text)) fields.new_status = "realizada";
      else if (/pendiente/i.test(text))       fields.new_status = "pendiente";
      break;
    }

    default:
      break;
  }

  return fields;
}

/* ─── Missing field prompts ───────────────────────────── */

const FIELD_PROMPTS = {
  contact_name:   "¿Para quién es esta acción? (nombre de la persona o familia)",
  date:           "¿Cuándo ocurrió? (ej: hoy, ayer, 2026-05-18)",
  followup_date:  "¿Para qué fecha quieres programar el seguimiento?",
  visit_type:     "¿Qué tipo de visita fue? (pastoral, seguimiento, bienestar, evangelismo)",
  prayer_type:    "¿Qué tipo de oración? (sanidad, familia, trabajo, espiritual, peticion_general)",
  title:          "¿Cuál es el título del evento?",
  event_type:     "¿Qué tipo de evento? (ej: culto, reunión, retiro)",
  content:        "¿Qué quieres escribir en la nota?",
  entity_id:      "¿A qué familia o persona corresponde esta nota?",
  entity_type:    "¿Es una nota para una familia o una persona?",
  visit_id:       "¿Cuál es el ID de la visita a actualizar?",
  new_status:     "¿A qué estado quieres cambiar la visita? (realizada, pendiente)",
  person_id:      "¿A qué persona se refiere?",
  status_field:   "¿Qué estado deseas actualizar? (baptism_status, spiritual_status)",
  new_value:      "¿Cuál es el nuevo valor para ese estado?",
};

export function getMissingFieldPrompt(field) {
  return FIELD_PROMPTS[field] ?? `¿Cuál es el valor de "${field}"?`;
}

/* ─── Public API ─────────────────────────────────────── */

/**
 * @typedef {Object} MutationIntentResult
 * @property {string|null}  toolId
 * @property {number}       confidence      — 0–1
 * @property {Object}       extractedFields — fields inferred from text + entityContext
 * @property {string[]}     missingFields   — required fields still needed
 * @property {boolean}      isReadyToConfirm — true when all required fields present
 */

/**
 * Resolve mutation intent from natural language.
 * Returns null if no action intent detected.
 *
 * @param {string} text — user message
 * @param {Object} [entityContext] — from KeviaContext
 * @param {Object} [appScope]
 * @returns {MutationIntentResult | null}
 */
export function resolveMutationIntent(text, entityContext = null, appScope = {}) {
  if (!text?.trim()) return null;

  // Find best matching pattern
  let bestMatch = null;
  for (const { toolId, patterns, confidence } of INTENT_PATTERNS) {
    if (patterns.some((p) => p.test(text))) {
      // Role check — skip if role not allowed
      const schema = getMutationSchemaById(toolId);
      if (!schema) continue;
      const role = appScope?.role ?? "*";
      if (!schema.allowedRoles.includes("*") && !schema.allowedRoles.includes(role)) continue;

      if (!bestMatch || confidence > bestMatch.confidence) {
        bestMatch = { toolId, confidence };
      }
    }
  }

  if (!bestMatch) return null;

  const { toolId, confidence } = bestMatch;
  const extractedFields = extractFields(toolId, text, entityContext);
  const missingFields   = getMissingFields(toolId, extractedFields);

  return {
    toolId,
    confidence,
    extractedFields,
    missingFields,
    isReadyToConfirm: missingFields.length === 0,
  };
}
